import { createContext, useContext, useMemo, useState } from 'react';

import origDayjs from 'dayjs';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { IoGrid } from 'react-icons/io5';
import { TiThList } from 'react-icons/ti';
import { Tooltip } from 'ui/components/common';
import { cn } from 'utils/helpers';
import { dayjs } from 'utils/providers/Timezone';
import { Event } from 'utils/types';

import { EventUpcomingBoard, EventUpcomingList } from '.';
import { useGetEventUpcoming } from '../hooks';

type CONTEXT = {
  isBoardView?: boolean;
  setIsBoardView?: React.Dispatch<React.SetStateAction<boolean>>;
  setDate?: React.Dispatch<React.SetStateAction<origDayjs.Dayjs>>;
  date?: origDayjs.Dayjs;
  isLoading?: boolean;
  data?: Event[];
};

const EventUpcomingContext = createContext<CONTEXT>({});
// eslint-disable-next-line react-refresh/only-export-components
export const useEventUpcomingContext = () => useContext(EventUpcomingContext);

const EventHeader = () => {
  const { isBoardView, setIsBoardView, setDate, date, isLoading } =
    useEventUpcomingContext();

  const handleOnNavigate = (value: number) => {
    let newDate = dayjs(date).add(1, 'days');

    if (value < 0) {
      newDate = dayjs(date).subtract(1, 'days');
    }

    setDate?.(newDate);
  };

  const currentDate = useMemo(() => {
    if (dayjs(date).isToday())
      return <span className="text-xs font-thin italic pl-2">(today)</span>;

    return null;
  }, [date]);

  return (
    <div className="flex items-center my-4 pb-6 pt-2 border-b border-b-disabled">
      <div className="flex space-x-2 items-center flex-1">
        <div className="flex space-x-2">
          <button
            type="button"
            className={cn(
              'border',
              'border-disabled',
              'bg-fill-tertiary',
              'rounded-lg',
              'text-content',
              'hover:text-primary-base',
              'p-2',
            )}
            disabled={isLoading}
            onClick={() => handleOnNavigate(-1)}
          >
            <BiChevronLeft className="text-2xl" />
          </button>
          <button
            type="button"
            className={cn(
              'border',
              'border-disabled',
              'bg-fill-tertiary',
              'rounded-lg',
              'text-content',
              'hover:text-primary-base',
              'p-2',
            )}
            disabled={isLoading}
            // TODO: Get back to this shortly
            onClick={() => handleOnNavigate(1)}
          >
            <BiChevronRight className="text-2xl" />
          </button>
        </div>
        <p className="text-base font-semibold text-[#022641]">
          {date?.format('DD MMM YYYY')}
          <span className="p-2">|</span>
          {date?.format('dddd')}
          {currentDate}
        </p>
      </div>
      <Tooltip
        visibilityBehavior="autoplacement"
        theme={'dark'}
        title={`${isBoardView ? 'List' : 'Board'} view`}
      >
        <button type="button" onClick={() => setIsBoardView?.(!isBoardView)}>
          {isBoardView ? (
            <IoGrid className="h-5 w-5 hover:text-primary-base transition text-content" />
          ) : (
            <TiThList className="h-5 w-5 hover:text-primary-base transition text-content" />
          )}
        </button>
      </Tooltip>
    </div>
  );
};

const EventUpcoming = () => {
  const [date, setDate] = useState(dayjs());
  const [isBoardView, setIsBoardView] = useState(false);

  const { data, isLoading } = useGetEventUpcoming({
    date: dayjs(date).toISOString(),
  });

  const value = useMemo(
    () => ({
      date,
      isBoardView,
      setDate,
      setIsBoardView,
      data,
      isLoading,
    }),
    [date, isBoardView, data, isLoading],
  );

  return (
    <EventUpcomingContext.Provider value={value}>
      <div className="grid gap-1">
        <EventHeader />
        {isBoardView ? <EventUpcomingBoard /> : <EventUpcomingList />}
      </div>
    </EventUpcomingContext.Provider>
  );
};

export default EventUpcoming;
